import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useMutation, useQueryClient } from "react-query";
import { updateShopifyCustomer, updateKlaviyoCustomer } from "@services/account/updateCustomer";
import { TextInput, PhoneInput } from "../Inputs";
import { COUNTRY_OPTIONS, ERROR_MSG } from "./lib/constants";
import { addCountryCode, removeCountryCode } from "./lib/phoneNumberFormatters";
import styles from "./styles/BasicInfo.module.css";

const EditButton = ({ setShowForm }) => {
  return (
    <button className={styles.editButton} onClick={() => setShowForm(true)}>
      Edit
    </button>
  );
};

const Cancel = ({ setShowForm }) => {
  return (
    <input
      className={`${styles.button} ${styles.secondary}`}
      type="button"
      value="Cancel"
      onClick={() => setShowForm(false)}
    />
  );
};

const Save = ({ handleSubmit }) => {
  return (
    <input
      className={`${styles.button} ${styles.primary}`}
      type="button"
      value="Save"
      onClick={handleSubmit}
    />
  );
};

const Form = ({ account, setShowForm, setSuccess }) => {
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const [error, setError] = useState(null);
  const [inputError, setInputError] = useState({
    firstName: false,
    lastName: false,
    phoneNumber: false,
  });
  const [countryCode, setCountryCode] = useState("CA");
  const [customer, setCustomer] = useState({
    firstName: account.customer.firstName,
    lastName: account.customer.lastName,
    email: account.customer.email,
    phone: removeCountryCode(account.customer.phone),
  });

  useEffect(() => {
    COUNTRY_OPTIONS.map((country) => {
      if (!account.customer.phone) return;

      if (account.customer.phone.startsWith(country.value)) {
        setCountryCode(country.code);
      }

      if (account.customer.phone.startsWith("+1")) {
        setCountryCode("CA");
      }
    });
  }, []);

  const queryToken = ["getAccountInfo", session.customerAccessToken];

  const handleChange = (name, e) => setCustomer({ ...customer, [name]: e.target.value });
  const handleChangeCountry = (value) => setCountryCode(value);
  const updateShopifyFn = (customer) =>
    updateShopifyCustomer(session.customerAccessToken, customer);
  const updateKlaviyoFn = (customer) => updateKlaviyoCustomer(customer);

  const shopifyUpdate = useMutation(updateShopifyFn, {
    onSettled: (data, error) => {
      if (error) {
        console.log(error);
        setError(ERROR_MSG);
      }

      if (data.customerUpdate.customer === null) {
        setInputError({ ...inputError, phoneNumber: true });
      }
    },
  });

  const klaviyoUpdate = useMutation(updateKlaviyoFn, {
    onMutate: async (updatedCustomer) => {
      await queryClient.cancelQueries(queryToken);
      const previous = queryClient.getQueryData(queryToken);

      queryClient.setQueryData(queryToken, (old) => ({
        customer: { ...old.customer, ...updatedCustomer },
      }));

      return { previous };
    },
    onSettled: (data, error, variables, context) => {
      if (data.body === 0 || error) {
        queryClient.setQueryData(queryToken, context.previous);
        setError(ERROR_MSG);
        console.log("[klaviyo]: error updating profile");
      }

      if (data.body === 1) {
        setSuccess(true);
        setShowForm(false);
      }
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (customer.firstName === null || customer.firstName === "") {
      setInputError({ ...inputError, firstName: true });
    }

    if (customer.lastName === null || customer.lastName === "") {
      setInputError({ ...inputError, lastName: true });
    }

    if (
      customer.firstName === null ||
      customer.firstName === "" ||
      customer.lastName === null ||
      customer.lastName === ""
    ) {
      return;
    }

    const phone = addCountryCode(customer.phone, countryCode);
    const updatedCustomer = { ...customer, phone };

    const shopifyRes = await shopifyUpdate.mutateAsync({ phone });

    if (shopifyRes.customerUpdate.customer === null) {
      setInputError({ ...inputError, phoneNumber: true });
      return;
    }

    klaviyoUpdate.mutate(updatedCustomer);
  };

  return (
    <form className={styles.form}>
      <section className={styles.formName}>
        <TextInput
          type="text"
          label="First Name"
          name="firstName"
          value={customer.firstName}
          onChange={(e) => handleChange("firstName", e)}
          error={inputError.firstName}
          errorMsg="First name is required"
        />
        <TextInput
          type="text"
          label="Last Name"
          name="lastName"
          value={customer.lastName}
          onChange={(e) => handleChange("lastName", e)}
          error={inputError.lastName}
          errorMsg="Last name is required"
        />
      </section>
      <TextInput
        type="email"
        label="Email"
        name="email"
        value={customer.email}
        onChange={(e) => handleChange("email", e)}
        disabled
      />
      <PhoneInput
        value={customer.phone ? customer.phone : ""}
        options={COUNTRY_OPTIONS}
        onChange={(e) => handleChange("phone", e)}
        onChangeCountry={handleChangeCountry}
        countryCode={countryCode}
        error={inputError.phoneNumber}
        errorMsg="Phone number is invalid"
      />
      <section className={styles.formControls}>
        <Cancel setShowForm={setShowForm} />
        <Save handleSubmit={handleSubmit} />
      </section>
      {error && <p className={styles.error}>{error}</p>}
    </form>
  );
};

export const BasicInfo = ({ account, setSuccess }) => {
  const [showForm, setShowForm] = useState(false);

  const DisplayInfo = () => {
    return (
      <section className={styles.displayInfo}>
        <section>
          <p>
            {account.customer.firstName} {account.customer.lastName}
          </p>
          <p>{account.customer.email}</p>
          <p>{account.customer.phone}</p>
        </section>
        {!showForm && <EditButton setShowForm={setShowForm} />}
      </section>
    );
  };

  return (
    <section>
      <DisplayInfo />
      {showForm && <Form account={account} setShowForm={setShowForm} setSuccess={setSuccess} />}
    </section>
  );
};
